import { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MapPin, ExternalLink, UserPlus, Check, Clock, Loader2, Flag, ShieldOff, Handshake, Star } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, useLocation } from "react-router-dom";
import { profileBackTarget } from "@/lib/profileBackTarget";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/hooks/useAuth";
import { useFriendRequests } from "@/hooks/useFriendRequests";
import { AboutMeDisplay } from "@/components/AboutMeDisplay";
import { SendFriendRequestDialog } from "@/components/SendFriendRequestDialog";
import { SaveProfileButton } from "@/components/SaveProfileButton";
import { UserSponsorBadge } from "@/components/UserSponsorBadge";
import { ReportUserDialog } from "@/components/ReportUserDialog";
import { BlockUserDialog } from "@/components/BlockUserDialog";
import { useUserBlocks } from "@/hooks/useUserBlocks";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";

interface NapTime {
  start: string;
  end: string;
}

interface UserProfile {
  id: string;
  first_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  district: string | null;
  parent_type: string | null;
  role: string | null;
  work_status: string | null;
  breastfeeding: boolean | null;
  single_parent: boolean | null;
  nap_times: NapTime[] | null;
  languages: string[] | null;
  experience_level: string | null;
  qualifications: string[] | null;
  availability_type: string | null;
  open_to_meet: boolean | null;
  children_info?: Array<{ age_months?: number; birth_ym?: string }> | null;
}

export interface ActivityContext {
  activityName: string;
  venueName?: string | null;
  plannedTime?: string | null;
  confidence?: string | null;
  note?: string | null;
  kind: "going" | "interested";
}

interface UserProfilePopupProps {
  userId: string;
  children: React.ReactNode;
  activityContext?: ActivityContext;
}

const confidenceLabel = (c?: string | null) => {
  if (c === "definitely") return "Definitely going";
  return "Planning to go";
};

export const UserProfilePopup = ({ userId, children, activityContext }: UserProfilePopupProps) => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [friendRequestDialogOpen, setFriendRequestDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const isMobile = useIsMobile();
  const { user } = useAuth();

  const { friendRequest, isFriend, loading: friendLoading, sendFriendRequest, businessGuardDialog: friendBusinessDialog, businessGuard } = useFriendRequests(userId);

  const isOwnProfile = user?.id === userId;
  const { blockUser, isBlocked: isBlockedFn } = useUserBlocks();

  const fetchProfile = async () => {
    if (profile || loading) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('public_profiles')
        .select('id, first_name, avatar_url, bio, district, parent_type, role, work_status, breastfeeding, single_parent, nap_times, languages, experience_level, qualifications, availability_type, open_to_meet, children_info')
        .eq('id', userId)
        .single();

      if (!error && data) {
        setProfile({
          ...data,
          nap_times: data.nap_times as unknown as NapTime[] | null,
        } as UserProfile);
      }
    } catch (e) {
      console.error('Error fetching profile:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSendRequest = async (note?: string) => {
    await sendFriendRequest(userId, note);
  };

  const FriendButton = () => {
    if (isOwnProfile || !user) return null;

    if (isFriend) {
      return (
        <Button variant="outline" className="text-sm w-full h-9" disabled>
          <Check className="h-4 w-4 mr-1" />
          Friends
        </Button>
      );
    }

    if (friendRequest?.status === 'pending') {
      return (
        <Button variant="outline" className="text-sm w-full h-9" disabled>
          <Clock className="h-4 w-4 mr-1" />
          {friendRequest.sender_id === user.id ? 'Hi sent · waiting' : 'They said hi \u2014 respond above'}
        </Button>
      );
    }

    return (
      <Button
        variant="default"
        className="text-sm w-full h-9"
        onClick={(e) => {
          e.stopPropagation();
          if (businessGuard("to say hi")) return;
          setFriendRequestDialogOpen(true);
        }}
      >
        <span className="mr-1.5">🌸</span>
        Say hi
      </Button>
    );
  };

  const ProfileContent = () => {
    const ctx = activityContext;
    const isGoing = ctx?.kind === "going";
    const name = profile?.first_name || "They";
    return (
      <div className="flex flex-col">
        {/* Hero header with gradient + context */}
        <div
          className={`relative -mx-6 -mt-6 px-6 pt-6 pb-12 ${
            isGoing
              ? "bg-gradient-to-br from-emerald-50 to-emerald-100/60 dark:from-emerald-950/40 dark:to-emerald-900/20"
              : ctx
                ? "bg-gradient-to-br from-amber-50 to-amber-100/60 dark:from-amber-950/40 dark:to-amber-900/20"
                : "bg-gradient-to-br from-primary/10 to-primary/5"
          }`}
        >
          {ctx && (
            <div className="flex items-start gap-2 mb-2">
              <div
                className={`shrink-0 h-7 w-7 rounded-full flex items-center justify-center ${
                  isGoing ? "bg-emerald-500 text-white" : "bg-amber-500 text-white"
                }`}
              >
                {isGoing ? <Check className="h-4 w-4" strokeWidth={3} /> : <Star className="h-4 w-4 fill-white" />}
              </div>
              <p className={`text-sm font-medium leading-snug ${isGoing ? "text-emerald-900 dark:text-emerald-100" : "text-amber-900 dark:text-amber-100"}`}>
                {isGoing ? `${name} is going to ` : `${name} is interested in `}
                <span className="font-bold">{ctx.activityName}</span>
                {isGoing && ctx.plannedTime ? ` at ${ctx.plannedTime}` : ""}
              </p>
            </div>
          )}
          {ctx && (isGoing || ctx.note) && (
            <p className={`text-xs leading-relaxed ml-9 ${isGoing ? "text-emerald-900/90 dark:text-emerald-100/90" : "text-amber-900/90 dark:text-amber-100/90"}`}>
              {isGoing && <span className="font-medium">{confidenceLabel(ctx.confidence)}</span>}
              {isGoing && ctx.note ? " · " : ""}
              {ctx.note && <span className="italic">"{ctx.note}"</span>}
            </p>
          )}
        </div>

        {/* Avatar overlapping the hero */}
        <div className="flex flex-col items-center -mt-10 px-1">
          <button
            type="button"
            onClick={() =>
              navigate(`/user/${userId}`, {
                state: { from: profileBackTarget(routerLocation.pathname, routerLocation.search) },
              })
            }
            className="rounded-full focus:outline-none focus:ring-2 focus:ring-primary/40"
            aria-label="View full profile"
          >
            <Avatar className="h-20 w-20 ring-4 ring-background shadow-lg cursor-pointer transition-transform hover:scale-105">
              {profile?.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt={profile.first_name || ''}
                  className="aspect-square h-full w-full object-cover"
                />
              ) : (
                <AvatarFallback className="text-2xl bg-primary/10 text-primary font-semibold">
                  {profile?.first_name?.[0] || '?'}
                </AvatarFallback>
              )}
            </Avatar>
          </button>


          <div className="text-center mt-3 space-y-2">
            <h3 className="text-lg font-bold text-foreground leading-tight">
              {profile?.first_name || 'User'}
            </h3>

            <div className="flex flex-wrap items-center justify-center gap-1.5">
              {profile?.open_to_meet && (
                <span className="inline-flex items-center gap-1 bg-bright-fuschia text-white rounded-full px-2.5 py-1 text-xs font-semibold">
                  <Handshake className="h-3.5 w-3.5" />
                  Open to meet now
                </span>
              )}
              {profile?.parent_type && (
                <Badge variant="secondary" className="text-xs">
                  {profile.parent_type}
                </Badge>
              )}
            </div>

            {profile?.district && (
              <p className="text-sm text-muted-foreground flex items-center justify-center gap-1">
                <MapPin className="h-3.5 w-3.5" />
                {profile.district}
              </p>
            )}
            <div className="flex justify-center pt-1">
              <UserSponsorBadge userId={userId} userName={profile?.first_name || undefined} />
            </div>
            {profile?.bio && (
              <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3 max-w-[260px] mx-auto pt-1">
                {profile.bio}
              </p>
            )}
          </div>

          <div className="w-full mt-4">
            <AboutMeDisplay
              role={profile?.role || undefined}
              parentType={profile?.parent_type}
              workStatus={profile?.work_status}
              breastfeeding={profile?.breastfeeding}
              singleParent={profile?.single_parent}
              napTimes={profile?.nap_times}
              district={profile?.district}
              languages={profile?.languages}
              experienceLevel={profile?.experience_level}
              qualifications={profile?.qualifications}
              availabilityType={profile?.availability_type}
              childrenInfo={profile?.children_info}
            />
          </div>

          <div className="w-full space-y-2 mt-4">
            {!isOwnProfile && user ? (
              <div className="flex items-center gap-2">
                <div className="flex-1"><FriendButton /></div>
                <SaveProfileButton userId={userId} userName={profile?.first_name || undefined} />
              </div>
            ) : (
              <FriendButton />
            )}
            <Button
              variant="outline"
              size="sm"
              className="text-sm w-full h-9"
              onClick={(e) => {
                e.stopPropagation();
                setDialogOpen(false);
                navigate(`/user/${userId}`, {
                  state: { from: profileBackTarget(routerLocation.pathname, routerLocation.search) },
                });
              }}
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
              View full profile
            </Button>

            {!isOwnProfile && user && (
              <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground pt-2">
                <button
                  onClick={(e) => { e.stopPropagation(); setBlockDialogOpen(true); }}
                  className="hover:text-foreground active:text-foreground transition-colors flex items-center gap-1.5 min-h-[44px] px-3"
                >
                  <ShieldOff className="h-4 w-4" />
                  Block
                </button>
                <span aria-hidden="true" className="text-muted-foreground">•</span>
                <button
                  onClick={(e) => { e.stopPropagation(); setReportDialogOpen(true); }}
                  className="hover:text-foreground active:text-foreground transition-colors flex items-center gap-1.5 min-h-[44px] px-3"
                >
                  <Flag className="h-4 w-4" />
                  Report
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Update FriendButton sizing too

  const LoadingContent = () => (
    <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin mr-2" />
      Loading...
    </div>
  );

  if (isMobile) {
    return (
      <>
        <span
          onClick={(e) => {
            e.stopPropagation();
            fetchProfile();
            setDialogOpen(true);
          }}
          className="cursor-pointer"
        >
          {children}
        </span>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-[360px] max-h-[85vh] overflow-y-auto p-6">
            <DialogHeader>
              <VisuallyHidden.Root>
                <DialogTitle>User Profile</DialogTitle>
              </VisuallyHidden.Root>
            </DialogHeader>
            {profile ? <ProfileContent /> : <LoadingContent />}
          </DialogContent>
        </Dialog>
        <SendFriendRequestDialog
          open={friendRequestDialogOpen}
          onOpenChange={setFriendRequestDialogOpen}
          recipientName={profile?.first_name || 'User'}
          onSend={handleSendRequest}
        />
        <ReportUserDialog
          open={reportDialogOpen}
          onOpenChange={setReportDialogOpen}
          reportedUserId={userId}
          reportedUserName={profile?.first_name || 'User'}
        />
        <BlockUserDialog
          open={blockDialogOpen}
          onOpenChange={setBlockDialogOpen}
          userName={profile?.first_name || 'User'}
          onConfirm={async () => await blockUser(userId)}
        />
        {friendBusinessDialog}
      </>
    );
  }

  return (
    <>
      <HoverCard openDelay={200} closeDelay={100}>
        <HoverCardTrigger asChild>
          <span
            onMouseEnter={fetchProfile}
            className="cursor-pointer"
            onClick={(e) => e.stopPropagation()}
          >
            {children}
          </span>
        </HoverCardTrigger>
        <HoverCardContent className="w-80 p-6" align="start">
          {profile ? <ProfileContent /> : <LoadingContent />}
        </HoverCardContent>
      </HoverCard>
      <SendFriendRequestDialog
        open={friendRequestDialogOpen}
        onOpenChange={setFriendRequestDialogOpen}
        recipientName={profile?.first_name || 'User'}
        onSend={handleSendRequest}
      />
      <ReportUserDialog
        open={reportDialogOpen}
        onOpenChange={setReportDialogOpen}
        reportedUserId={userId}
        reportedUserName={profile?.first_name || 'User'}
      />
      <BlockUserDialog
        open={blockDialogOpen}
        onOpenChange={setBlockDialogOpen}
        userName={profile?.first_name || 'User'}
        onConfirm={async () => await blockUser(userId)}
      />
      {friendBusinessDialog}
    </>
  );
};